import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { AjustesClientes } from '../../../api/ops/clientesPrivacidad';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Ayuda } from '../../../componentes/Ayuda';
import { Campo } from '../../../componentes/Campo';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { Interruptor } from '../../../componentes/Interruptor';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { estadoDeError } from '../../../logica/estados';
import { textoDeError } from '../../equipo/textos';
import { AYUDA, PLAZOS } from './textos';

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    soloLectura: boolean;
    abierto: boolean;
    onCerrar: () => void;
}

interface Borrador {
    notasNoCaducan: boolean;
    notasMeses: string;
    fichasMeses: string;
}

function borradorDe(a: AjustesClientes): Borrador {
    return { notasNoCaducan: a.notes_retention_months === null, notasMeses: String(a.notes_retention_months ?? 24), fichasMeses: String(a.profile_retention_months) };
}

function meses(texto: string, min: number, max: number): number | null {
    if (!/^\d{1,3}$/.test(texto.trim())) {
        return null;
    }
    const n = Number(texto.trim());
    return n >= min && n <= max ? n : null;
}

/**
 * «Plazos de conservación» de 2.7 (G194, G204; sin dibujo: mismo lenguaje que el resto y textos a validar): caducidad de
 * las notas internas (de 1 a 120 meses, o sin caducidad) y meses sin visitas tras los que se anonimiza una ficha (de 12 a
 * 120, 36 por defecto). Se leen con `getCustomerSettings` y su `ETag` y se guardan con `updateCustomerSettings` e
 * `If-Match`: si otra persona los cambió antes (412), se vuelven a leer. En solo lectura no se cambian.
 */
export function PanelPlazos({ ops, negocio, movil, soloLectura, abierto, onCerrar }: Props) {
    const avisar = useAvisoBreve();
    const [leidos, setLeidos] = useState<{ ajustes: AjustesClientes; version: string | null } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [b, setB] = useState<Borrador>({ notasNoCaducan: true, notasMeses: '24', fichasMeses: '36' });
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vivo = true;
        ops.getCustomerSettings(negocio)
            .then((r) => {
                if (vivo) {
                    setLeidos({ ajustes: r.datos, version: r.version });
                    setB(borradorDe(r.datos));
                    setError(null);
                    setIntentado(false);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, abierto, intento]);

    const notas = b.notasNoCaducan ? null : meses(b.notasMeses, 1, 120);
    const fichas = meses(b.fichasMeses, 12, 120);
    const errorNotas = !b.notasNoCaducan && notas === null ? 'Escribe de 1 a 120 meses.' : null;
    const errorFichas = fichas === null ? 'Escribe de 12 a 120 meses.' : null;
    const editable = !soloLectura && leidos !== null;

    const guardar = () => {
        setIntentado(true);
        if (leidos === null || errorNotas !== null || errorFichas !== null || fichas === null) {
            return;
        }
        setOcupado(true);
        ops.updateCustomerSettings(negocio, { notes_retention_months: notas, profile_retention_months: fichas }, leidos.version ?? '*')
            .then((r) => {
                setLeidos({ ajustes: r.datos, version: r.version });
                avisar({ tipo: 'ok', texto: PLAZOS.guardado });
                onCerrar();
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'version_conflict') {
                    avisar({ tipo: 'error', texto: PLAZOS.cambiado });
                    setIntento((i) => i + 1);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido guardar los plazos. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <PanelLateral abierto={abierto} onCerrar={onCerrar} movil={movil} titulo={PLAZOS.titulo} eyebrow="Protección de datos" subtitulo={PLAZOS.subtitulo} icono="clock" accion={editable ? 'Guardar' : ''} ocupado={ocupado} onAccion={guardar}>
            <div className="rv-ca-cuerpo">
                <div className="rv-pr-panel-cab">
                    <span />
                    <Ayuda tarjetas={AYUDA.plazos} />
                </div>
                {leidos === null ? (
                    error !== null ? (
                        <EstadoError seccion="los plazos" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />
                    ) : (
                        <EstadoCargando seccion="los plazos" />
                    )
                ) : (
                    <>
                        {soloLectura && (
                            <p className="rv-ca-nota">
                                <Icono nombre="eye" />
                                <span>{PLAZOS.soloLectura}</span>
                            </p>
                        )}
                        <div className="rv-ca-bloque">
                            <strong className="rv-pr-subtitulo">{PLAZOS.notas}</strong>
                            <div className="rv-pr-interruptor">
                                <Interruptor etiqueta={PLAZOS.notasNoCaducan} ayuda={PLAZOS.notasNoCaducanAyuda} valor={b.notasNoCaducan} bloqueado={!editable} linea="ninguna" onChange={(v) => { setB({ ...b, notasNoCaducan: v }); }} />
                            </div>
                            {!b.notasNoCaducan && (
                                <Campo etiqueta={PLAZOS.notasMeses} tipo="number" modo="numeric" valor={b.notasMeses} onChange={(v) => { setB({ ...b, notasMeses: v }); }} bloqueado={!editable} ayuda={PLAZOS.notasMesesAyuda} error={intentado ? errorNotas : null} max={3} nombre="meses-notas" />
                            )}
                        </div>
                        <div className="rv-ca-bloque">
                            <strong className="rv-pr-subtitulo">{PLAZOS.fichas}</strong>
                            <Campo etiqueta={PLAZOS.fichasMeses} tipo="number" modo="numeric" valor={b.fichasMeses} onChange={(v) => { setB({ ...b, fichasMeses: v }); }} bloqueado={!editable} ayuda={PLAZOS.fichasMesesAyuda} error={intentado ? errorFichas : null} max={3} nombre="meses-fichas" />
                        </div>
                    </>
                )}
            </div>
        </PanelLateral>
    );
}
